import React from 'react';
import { getRoomSRCStatusIconByLastSeenTimestamp } from '../../data/controllers/SRCStatus';
import translate from '@data/translations';
import Tooltip from '@components/tooltip/tooltip';
import { getHWIconDescriptor } from './hwIconDescriptor';
import RfidToggleIcon from './rfidToggleIcon';
import './hwRoomViewModern.css';

const getTempValueClass = (value) => {
    if (value === undefined || value === null) {
        return '';
    }
    if (value > 30) {
        return 'hot';
    }
    if (value > 25) {
        return 'warm';
    }
    return 'cool';
};

const getRoomModeBadgeClass = (roomModeLabel, roomModeInterpreter) => {
    if (!roomModeLabel) {
        return '';
    }
    if (roomModeLabel === 'CLN') {
        return 'cleaning';
    }
    const entry = Object.entries(roomModeInterpreter || {}).find(([, label]) => label === roomModeLabel);
    if (!entry) {
        return '';
    }
    if (entry[0] === 'ECO') {
        return 'eco';
    }
    if (entry[0] === 'PRECOMFORT') {
        return 'precomfort';
    }
    return '';
};

const ModernHWCard = ({
    history,
    dashboardObj: i,
    alarmInterpreter,
    handleShowBasicOptions,
    areTemperatureValuesShown,
    rooms,
    enforceRFIDCardCheckMap,
    onToggleEnforceRFIDCardCheck,
    showWireSensorValues,
    showRFIDCheckFeature,
}) => {
    const inputInterpreter = alarmInterpreter?.inputInterpreter ? alarmInterpreter.inputInterpreter : [];
    const enabledInterpreters = inputInterpreter.filter((ei) => ei.enabled);
    const roomModeInterpreter = alarmInterpreter?.roomModeInterpreter ? alarmInterpreter.roomModeInterpreter : {};
    const lastReadoutTs = i.thReadoutTimestamp ? i.thReadoutTimestamp : null;
    const lastWireReadoutTs = i.wireReadoutTimestamp ? i.wireReadoutTimestamp : null;
    const room = rooms?.find((r) => r?.id === i?.roomInfoId);
    const rfidValue = enforceRFIDCardCheckMap?.[i.roomDeviceId];

    return (
        <div className="hwm-card">
            <div className="hwm-id-row">
                <div className="hwm-id-left" title={i.roomName}>
                    {getRoomSRCStatusIconByLastSeenTimestamp(i.lastSeenTimestamp)}
                    <span>
                        {room && room.remoteDeviceId && (room.remoteDeviceId2 || room.remoteDeviceId3)
                            ? i.roomName + ' (' + i.roomDevicePosition + ')'
                            : i.roomName}
                    </span>
                </div>
                {i.roomMode ? (
                    <span
                        className={
                            'hwm-status-badge ' + getRoomModeBadgeClass(i.roomMode, roomModeInterpreter)
                        }
                    >
                        {i.roomMode}
                    </span>
                ) : null}
            </div>

            <div className="hwm-icons-row">
                {enabledInterpreters.map((ei, indx) => {
                    if (ei.name === 'cleaningServiceInput') {
                        const isCleanedToday = i.lastCleaningDate === new Date().toISOString(true).substr(0, 10);
                        return (
                            <div key={indx} className="hwm-icon-wrap" title={translate(ei.name)}>
                                <i
                                    className="fa fa-female"
                                    style={{ color: isCleanedToday ? '#0f7b3a' : undefined }}
                                />
                            </div>
                        );
                    }
                    const descriptor = getHWIconDescriptor(i, ei.name, alarmInterpreter);
                    if (!descriptor) {
                        return null;
                    }
                    return (
                        <div key={indx} className="hwm-icon-wrap" title={descriptor.title}>
                            <i className={descriptor.iconClass} style={{ color: descriptor.color }} />
                        </div>
                    );
                })}

                <div className="hwm-actions-row">
                    {showRFIDCheckFeature && rfidValue !== undefined ? (
                        <Tooltip
                            message={
                                translate('Force checking disposed RFID card') +
                                ': ' +
                                translate(rfidValue ? 'DISABLE' : 'ENABLE')
                            }
                            style={{ padding: '3px', right: '100%', top: '-5px', marginRight: '5px' }}
                        >
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleEnforceRFIDCardCheck(i.roomDeviceId, rfidValue);
                                }}
                                className={'hwm-icon-wrap clickable' + (rfidValue ? ' rfid-button-active' : '')}
                            >
                                <RfidToggleIcon enabled={!!rfidValue} inButton variant="signal" />
                            </div>
                        </Tooltip>
                    ) : null}

                    <Tooltip
                        message={translate('Guest access codes')}
                        style={{ padding: '3px', right: '100%', top: '-5px', marginRight: '5px' }}
                    >
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShowBasicOptions(i.roomInfoId, i.roomName, i.roomDeviceId);
                            }}
                            className="hwm-icon-wrap clickable"
                        >
                            <i className="fa fa-key"></i>
                        </div>
                    </Tooltip>

                    <Tooltip
                        message={translate('Room event history')}
                        style={{ padding: '3px', right: '100%', top: '-10px', marginRight: '25px' }}
                    >
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                history.push('/roomEventHistory/' + i.roomInfoId + '/' + i.roomDeviceId);
                            }}
                            className="hwm-icon-wrap clickable"
                        >
                            <i className="fa fa-list"></i>
                        </div>
                    </Tooltip>
                </div>
            </div>

            {areTemperatureValuesShown ? (
                <div className="hwm-temp-row">
                    <div className="hwm-temp-item">
                        <span className="hwm-temp-label">T1</span>
                        <span className={'hwm-temp-value ' + getTempValueClass(i.lastTh1Readout)}>
                            {i.lastTh1Readout ?? '-'}
                        </span>
                    </div>
                    <div className="hwm-temp-item">
                        <span className="hwm-temp-label">T2</span>
                        <span className={'hwm-temp-value ' + getTempValueClass(i.lastTh2Readout)}>
                            {i.lastTh2Readout ?? '-'}
                        </span>
                    </div>
                    <div className="hwm-temp-item">
                        <span className="hwm-temp-label">T3</span>
                        <span className={'hwm-temp-value ' + getTempValueClass(i.lastTh3Readout)}>
                            {i.lastTh3Readout ?? '-'}
                        </span>
                    </div>
                    <span className="hwm-last-read">
                        {lastReadoutTs ? `Last read: ${new Date(lastReadoutTs).toLocaleString()}` : 'Last read: -'}
                    </span>
                </div>
            ) : null}

            {areTemperatureValuesShown && showWireSensorValues ? (
                <div className="hwm-temp-row">
                    <div className="hwm-temp-item">
                        <span className="hwm-temp-label">1WT1</span>
                        <span className={'hwm-temp-value ' + getTempValueClass(i?.lastWire1Readout)}>
                            {i?.lastWire1Readout ?? '-'}
                        </span>
                    </div>
                    <div className="hwm-temp-item">
                        <span className="hwm-temp-label">1WT2</span>
                        <span className={'hwm-temp-value ' + getTempValueClass(i?.lastWire2Readout)}>
                            {i?.lastWire2Readout ?? '-'}
                        </span>
                    </div>
                    <div className="hwm-temp-item">
                        <span className="hwm-temp-label">1WT3</span>
                        <span className={'hwm-temp-value ' + getTempValueClass(i?.lastWire3Readout)}>
                            {i?.lastWire3Readout ?? '-'}
                        </span>
                    </div>
                    <span className="hwm-last-read">
                        {lastWireReadoutTs
                            ? `Last read: ${new Date(lastWireReadoutTs).toLocaleString()}`
                            : 'Last read: -'}
                    </span>
                </div>
            ) : null}
        </div>
    );
};

const ModernHWGrid = ({ srcStatusDashboard, columnsNumber, ...cardProps }) => {
    return (
        <div className="hw-modern-view">
            <div className="hwm-grid" style={{ gridTemplateColumns: `repeat(${columnsNumber}, 1fr)` }}>
                {srcStatusDashboard.map((dashboardObj, index) => (
                    <ModernHWCard key={index} dashboardObj={dashboardObj} {...cardProps} />
                ))}
            </div>
        </div>
    );
};

export default ModernHWGrid;
